/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/components/FormGroup.tsx
 * Release: 1.0.1
 * Descrizione: Componente per gestire gruppi/sezioni del form con bordo dinamico
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { FormGroup as FormGroupType, FormValues, FormErrors } from '../types';
import { FormField } from './FormField';

interface FormGroupProps {
  group: FormGroupType;
  values: FormValues;
  errors: FormErrors;
  onValueChange: (fieldId: string, value: any) => void;
  onProgressionAttempt: (fieldId: string) => void;
  userPermissions: string[];
  enabled: boolean;
  forceOpen?: boolean;
  attemptedProgression: Set<string>;
  readOnly?: boolean;
}

function getSectionStatus(
  group: FormGroupType,
  values: FormValues,
  errors: FormErrors,
  enabled: boolean
): 'empty' | 'partial' | 'valid' | 'error' | 'warning' {
  let total = 0;
  let valid = 0;
  let filled = 0;
  let hasObbligError = false;
  let hasNonObbligError = false;
  let hasOnlyOptionalFields = true; // Flag per verificare se la sezione ha solo campi opzionali
  
  // Caso speciale per la sezione "Dati Azienda" - validazione fatturazione elettronica
  if (group.id === 'dati-azienda') {
    const countryCode = values.prefissoNazione;
    const codiceUnivoco = values.codiceUnivoco || '';
    const pec = values.pec || '';
    
    if (countryCode === 'IT') {
      // Per Italia: almeno uno dei due deve essere compilato
      const hasCodiceUnivoco = codiceUnivoco.trim().length > 0;
      const hasPec = pec.trim().length > 0;
      
      if (!hasCodiceUnivoco && !hasPec) {
        // Se nessuno dei due è compilato, la sezione ha un errore
        hasObbligError = true;
      }
    }
  }
  
  group.rows.forEach((row) => {
    row.columns.forEach((col) => {
      const v = values[col.id];
      const err = errors[col.id];
      
      // Se c'è almeno un campo obbligatorio, la sezione non ha solo campi opzionali
      if (col.field.required) {
        hasOnlyOptionalFields = false;
      }
      
      // Funzione per validare se un valore è considerato "valido"
      const isValidValue = (value: any, field: any): boolean => {
        if (!value) return false;
        
        // Per campi custom AddressSection, controlla se ha dati validi
        if (field.type === 'custom' && field.customComponent === 'AddressSection') {
          if (typeof value === 'object' && value !== null) {
            // Se ha il campo search compilato, è valido
            if (value.search && value.search.trim()) return true;
            // Altrimenti, deve avere i campi obbligatori
            return !!(value.via && value.citta && value.cap && value.provincia);
          }
          return false;
        }
        
        // Per altri tipi, usa la logica standard
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'boolean') return value;
        if (Array.isArray(value)) return value.length > 0;
        return !!value;
      };
      
      if (col.field.required) {
        total++;
        if (isValidValue(v, col.field) && !err) valid++;
        if (!isValidValue(v, col.field) || err) hasObbligError = hasObbligError || (!isValidValue(v, col.field) || !!err);
      }
      if (!col.field.required && err) hasNonObbligError = true;
      if (isValidValue(v, col.field)) filled++;
    });
  });
  
  if (!enabled) return 'empty';
  if (hasObbligError) return 'error';
  
  // Se la sezione ha solo campi opzionali e non ci sono errori, è valida
  if (hasOnlyOptionalFields && !hasNonObbligError) return 'valid';
  
  // Se la sezione ha solo campi opzionali ma ci sono errori, è warning
  if (hasOnlyOptionalFields && hasNonObbligError) return 'warning';
  
  // Per sezioni con campi obbligatori
  if (total > 0 && valid === total && hasNonObbligError) return 'warning';
  if (total > 0 && valid === total && !hasNonObbligError) return 'valid';
  if (filled > 0) return 'partial';
  return 'empty';
}

export const FormGroup: React.FC<FormGroupProps> = ({
  group,
  values,
  errors,
  onValueChange,
  onProgressionAttempt,
  userPermissions,
  enabled,
  forceOpen = false,
  attemptedProgression,
  readOnly
}) => {
  const [isCollapsed, setIsCollapsed] = useState(group.collapsed || false); // INIZIALIZZA CON IL VALORE CORRETTO
  const [isFocused, setIsFocused] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Verifica permessi
  const canView = group.permissions.view.some(perm => userPermissions.includes(perm));
  if (!canView) return null;

  // Se forceOpen è true, apri la sezione
  const shouldBeOpen = forceOpen || !isCollapsed;

  // Aggiorna lo stato interno quando forceOpen cambia
  React.useEffect(() => {
    if (forceOpen && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [forceOpen, isCollapsed]);





  // Debug: traccia cambiamenti di stato
  useEffect(() => {
    console.log('🔧 [FormGroup] Stato cambiato:', {
      groupId: group.id,
      groupTitle: group.title,
      isCollapsed,
      shouldBeOpen: !isCollapsed,
      timestamp: new Date().toISOString()
    });
  }, [isCollapsed, group.id, group.title]);



  const toggleCollapse = (e: React.MouseEvent) => {
    // PREVENZIONE TOTALE DEL REFRESH
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔧 [FormGroup] Toggle collapse chiamato:', {
      groupId: group.id,
      groupTitle: group.title,
      isCollapsed,
      willCollapse: !isCollapsed,
      hasCollapsible: !!group.collapsible,
      groupCollapsed: group.collapsed,
      timestamp: new Date().toISOString()
    });
    
    if (group.collapsible) {
      const newCollapsedState = !isCollapsed;
      console.log('🔧 [FormGroup] Cambiando stato da', isCollapsed, 'a', newCollapsedState);
      setIsCollapsed(newCollapsedState);
    }
  };

  // Stato validazione sezione
  const sectionStatus = getSectionStatus(group, values, errors, enabled);
  let borderColor = 'border-zinc-700';
  if (!enabled) borderColor = 'border-zinc-700';
  else if (isFocused) borderColor = 'border-white';
  else if (sectionStatus === 'valid') borderColor = 'border-green-500';
  else if (sectionStatus === 'error') borderColor = 'border-red-500';
  else if (sectionStatus === 'warning') borderColor = 'border-orange-400';
  else if (sectionStatus === 'partial') borderColor = 'border-yellow-400';

  const groupClass = `bg-zinc-800 rounded-lg border shadow-sm transition-colors duration-200 ${borderColor} ${!enabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`;

  return (
    <motion.div
      className={groupClass}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onClick={() => setIsFocused(true)}
    >
      {/* Header del gruppo */}
      <div
        ref={headerRef}
        className={`p-4 border-b border-zinc-700 ${
          group.collapsible ? 'cursor-pointer hover:bg-zinc-750' : ''
        }`}
        onClick={group.collapsible ? toggleCollapse : undefined}
        onMouseDown={(e) => {
          if (group.collapsible) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onKeyDown={(e) => {
          if (group.collapsible && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            e.stopPropagation();
            toggleCollapse(e as any);
          }
        }}
        role={group.collapsible ? 'button' : undefined}
        tabIndex={group.collapsible ? 0 : undefined}
        onPointerDown={(e) => {
          if (group.collapsible) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onTouchStart={(e) => {
          if (group.collapsible) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        style={{ userSelect: 'none' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {group.icon && React.createElement(group.icon, { className: "w-6 h-6" })}
            <div>
              {group.title && <h3 className="text-lg font-semibold text-white">{group.title}</h3>}
              {group.description && (
                <p className="text-sm text-zinc-400 mt-1">{group.description}</p>
              )}
            </div>
          </div>
          {group.collapsible && (
            <motion.div
              animate={{ rotate: isCollapsed ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="text-zinc-400"
              style={{ pointerEvents: 'none' }}
            >
              ▼
            </motion.div>
          )}
        </div>
      </div>

      {/* Contenuto del gruppo */}
      <AnimatePresence mode="wait" initial={false}>
        {shouldBeOpen && (
          <motion.div
            key={`group-content-${group.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {group.rows.map((row, rowIndex) => {
                // Controlla se questa è la riga che contiene Codice Univoco e PEC
                const isCodiceUnivocoPecRow = row.columns.some(col => 
                  col.id === 'codiceUnivoco' || col.id === 'pec'
                );
                
                // Controlla se deve mostrare il messaggio di errore per Codice Univoco/PEC
                const shouldShowCodiceUnivocoPecError = 
                  group.id === 'dati-azienda' && 
                  isCodiceUnivocoPecRow &&
                  attemptedProgression.has('dati-azienda') &&
                  values.prefissoNazione === 'IT' &&
                  (!values.codiceUnivoco || values.codiceUnivoco.trim() === '') &&
                  (!values.pec || values.pec.trim() === '');

                return (
                  <div key={row.id}>
                    <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4">
                      {row.columns.map((column, columnIndex) => {
                        // Calcola tabIndex sequenziale per ogni campo
                        const fieldTabIndex = (rowIndex * 100) + columnIndex + 1;
                        const columnWidth = column.width === 'full' ? 'col-span-full' :
                                          column.width === '1/2' ? 'col-span-1 md:col-span-3 lg:col-span-6' :
                                          column.width === '1/3' ? 'col-span-1 md:col-span-2 lg:col-span-4' :
                                          column.width === '2/3' ? 'col-span-1 md:col-span-4 lg:col-span-8' :
                                          column.width === '1/4' ? 'col-span-1 md:col-span-2 lg:col-span-3' :
                                          column.width === '1/5' ? 'col-span-1 md:col-span-1 lg:col-span-2' :
                                          column.width === '1/6' ? 'col-span-1 md:col-span-1 lg:col-span-2' :
                                          column.width === '1/12' ? 'col-span-1 lg:col-span-1' :
                                          column.width === '2/12' ? 'col-span-1 lg:col-span-2' :
                                          column.width === '3/12' ? 'col-span-1 lg:col-span-3' :
                                          column.width === '4/12' ? 'col-span-1 lg:col-span-4' :
                                          column.width === '3/4' ? 'col-span-1 md:col-span-4 lg:col-span-9' :
                                          column.width === '5/12' ? 'col-span-1 md:col-span-3 lg:col-span-5' :
                                          'col-span-1';



                        // Debug per paymentMethods e deliveryMethods
                        if (column.id === 'paymentMethods' || column.id === 'deliveryMethods') {
                          console.log(`🔍 [FormGroup] Campo ${column.id}:`, {
                            valueInValues: values[column.id],
                            allValuesKeys: Object.keys(values),
                            hasPaymentMethods: 'paymentMethods' in values,
                            hasDeliveryMethods: 'deliveryMethods' in values
                          });
                        }

                        return (
                          <div key={column.id} className={columnWidth}>
                            {(() => {
                              try {
                                const formFieldResult = (
                                  <FormField
                                    field={column.field}
                                    value={values[column.id]}
                                    error={errors[column.id]}
                                    onChange={(value) => onValueChange(column.id, value)}
                                    onFocus={() => onProgressionAttempt(column.id)}
                                    disabled={!enabled || readOnly}
                                    tabIndex={fieldTabIndex}
                                    readOnly={readOnly}
                                    formValues={values}
                                  />
                                );
                                
                                // Debug per verificare se FormField è stato renderizzato
                                if (column.field.type === 'custom') {
                                  console.log(`✅ [FormGroup] FORMFIELD RENDERIZZATO: ${column.field.label}`, {
                                    id: column.id,
                                    type: column.field.type,
                                    customComponent: column.field.customComponent,
                                    result: formFieldResult ? 'success' : 'null',
                                    DEBUG_TIMESTAMP: new Date().toISOString()
                                  });
                                }
                                
                                return formFieldResult;
                              } catch (error) {
                                console.error(`🚨 [FormGroup] Errore nel rendering FormField: ${column.field.label}`, {
                                  error,
                                  field: column.field,
                                  DEBUG_TIMESTAMP: new Date().toISOString()
                                });
                                return <div className="text-red-500">Errore nel rendering FormField</div>;
                              }
                            })()}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Messaggio di errore per Codice Univoco/PEC */}
                    {shouldShowCodiceUnivocoPecError && (
                      <div className="mt-3 px-4 py-2 bg-red-900/20 border border-red-500/30 rounded-lg">
                        <p className="text-sm text-red-400 font-medium">
                          Per l'Italia è obbligatorio compilare almeno uno dei due campi: Codice Univoco o PEC
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FormGroup; 